import { Badge, FadeIn, Skeleton } from "@xola/ui-kit";
import { isNil, pick } from "lodash";
import React from "react";
import { useQuery } from "react-query";
import { PaymentMethod } from "types";
import shallow from "zustand/shallow";
import { useSeller } from "../../../hooks/useAccount";
import { getEarningsReport, getRefundReserveReport } from "../../../reports/EarningsReport";
import {
    EarningsReportResponse,
    useEarningsReportFilterStore,
} from "../../../stores/reports/earningsReportFilterStore";
import { Currency } from "../../Common/Currency";
import { EarningsReportMethod } from "./EarningsReportMethod";

export const EarningsReportSummary = () => {
    const seller = useSeller();

    const [from, to] = useEarningsReportFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const filters = useEarningsReportFilterStore((state) => state.filters);

    const params = { seller, filters, from, to };
    const { data, isLoading } = useQuery(["earningsReport", params], async () => getEarningsReport(params), {
        staleTime: 100,
    });

    const { data: refundReserve } = useQuery<number>(
        ["refundReserveReport", params],
        async () => {
            const data = await getRefundReserveReport(params);
            return data?.aggregations?.total?.items?.stats_earning?.sum ?? 0;
        },
        { staleTime: 100 },
    );

    if (isLoading || !data || isNil(refundReserve)) {
        return <EarningsReportSkeleton />;
    }

    const { total, methods }: EarningsReportResponse = data;
    const hasRefundReserve = !isNil(seller.preferences?.payout?.escrow?.actualAmount);

    return (
        <FadeIn>
            <div className="w-full px-6 py-10">
                <div className="flex w-full flex-row justify-between space-x-3">
                    <div className="flex flex-col items-center justify-center space-y-2 rounded bg-green-lighter py-8 px-6">
                        <span className="font-bold">Total</span>
                        <div className="text-2xl font-bold" data-testid="earnings-report-summary-total">
                            <Currency.Split currency={seller.currency}>{total}</Currency.Split>
                        </div>
                        {hasRefundReserve && (
                            <Badge color="secondary" className="!rounded">
                                Refund Reserve:&nbsp;
                                <Currency>{refundReserve}</Currency>
                            </Badge>
                        )}
                    </div>
                    <div className="flex w-full flex-row flex-wrap justify-start rounded border border-gray-light py-9 px-8">
                        {methods.map((bucket) => {
                            if (bucket.method === PaymentMethod.CREDIT_CARD) {
                                return (
                                    <EarningsReportMethod
                                        key={bucket.method}
                                        method={bucket.method}
                                        total={bucket.total}
                                        meta={pick(bucket, ["paid", "commission"])}
                                    />
                                );
                            }

                            return (
                                <EarningsReportMethod key={bucket.method} method={bucket.method} total={bucket.total} />
                            );
                        })}
                    </div>
                </div>
            </div>
        </FadeIn>
    );
};

const EarningsReportSkeleton = () => (
    <div className="w-full px-6 py-10">
        <div className="flex h-32 w-full flex-row justify-between space-x-2">
            <div className="w-44">
                <Skeleton height={128} />
            </div>
            <div className="w-full">
                <Skeleton height={128} />
            </div>
        </div>
    </div>
);
